import React from 'react'
import { AmountDisplayProps } from './types'

import { BaseText, BaseView } from '@/components/base'
import { SkeletonShape } from '@/components/common'
import { currencyUtils } from '@/utils'

const { removeTrailingZeros } = currencyUtils

// Функция для извлечения символа валюты
function extractDynamicCurrencySymbol({
  currencyCode,
  shouldMaskStablecoinAsFiat = false,
}: {
  currencyCode: string
  shouldMaskStablecoinAsFiat?: boolean
}): string {
  // Упрощенная версия - можно расширить при необходимости
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    BTC: '₿',
    ETH: 'Ξ',
  }

  if (shouldMaskStablecoinAsFiat && currencyCode === 'USDT') {
    return '$'
  }

  return symbols[currencyCode] || ''
}

// Функция для проверки RTL валют
function checkIfDynamicCurrencyIsRTL({
  currencyCode,
}: {
  currencyCode: string
}): boolean {
  // Упрощенная версия - можно расширить при необходимости
  const rtlCurrencies = ['AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR']
  return rtlCurrencies.includes(currencyCode)
}

// Функция для проверки наличия символа
function containsSymbol(currencySymbol: string): boolean {
  return currencySymbol.length > 0
}

export const AmountDisplay: React.FC<AmountDisplayProps> = ({
  amount,
  currencyCode,
  triggerShake,
  containerProps,
  sign,
  skeletonActive,
  equivalentAmountFormatted,
  shouldRemoveTrailingZeros = false,
}) => {
  const currencySymbol = extractDynamicCurrencySymbol({
    currencyCode,
    shouldMaskStablecoinAsFiat: false,
  })
  const isRTL = checkIfDynamicCurrencyIsRTL({ currencyCode })

  const shouldRenderSmallValue = containsSymbol(currencySymbol) === false

  const adaptedAmount = shouldRemoveTrailingZeros
    ? removeTrailingZeros(amount)
    : amount

  const amountWithSymbol = `${!isRTL ? currencySymbol : ''}${adaptedAmount}${
    isRTL ? currencySymbol : ''
  }`

  if (skeletonActive) {
    return <SkeletonShape w={251} h={40} rounded={8} />
  }

  return (
    <BaseView justify="flex-end" {...containerProps}>
      {/* ShakeAnimation можно добавить позже если нужно */}
      <BaseView
        row
        align="baseline"
        style={{
          margin: '0 auto',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          // Добавляем анимацию тряски если triggerShake
          animation: triggerShake ? 'shake 0.5s' : undefined,
        }}
      >
        {sign && (
          <BaseText
            fontSize={48}
            fontFamily="semibold"
            color="label.default"
            style={{
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            {sign}
          </BaseText>
        )}
        <BaseText
          fontSize={48}
          fontFamily="semibold"
          color="label.default"
          style={{
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {shouldRenderSmallValue ? adaptedAmount || '0' : amountWithSymbol}
        </BaseText>

        {shouldRenderSmallValue && (
          <BaseText fontSize={18} fontFamily="semibold" color="label.default">
            {currencyCode}
          </BaseText>
        )}
      </BaseView>

      {/* {!!equivalentAmountFormatted && (
        <BaseText
          textAlign="center"
          color="label.description"
          fontFamily="medium"
        >
          ≈{equivalentAmountFormatted}
        </BaseText>
      )} */}

      {/* CSS для анимации тряски */}
      <style>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-2px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(2px);
          }
        }
      `}</style>
    </BaseView>
  )
}

export default AmountDisplay
